import React, { useState } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { Icon } from '@gumtree/ui-library';
import { qaAttribute } from '@gumtree/ui-library/src/utils/qa-service';
import {
    trackGA4AndNavigate,
    trackGA4Event,
    trackGA4UserLoginSuccess,
    trackGA4UserRegistrationSuccess,
} from '@gumtree/shared/src/util/ga4-shared';
import { getLinkHostname } from '@gumtree/shared/src/util/ga4-bff';

import { css } from '@emotion/react';

import { breakpoints, mediaQuery } from '@gumtree/ui-library/src/base/theme';
import type { ShellState, ShellUseDispatch } from '../../reducers/common';
import * as searchBarAct from '../search-bar/search-bar-actions';

import SideMenu from '../side-menu/side-menu';
import UserMenu from '../user-menu/user-menu';
import AccountMenu from '../account-menu/account-menu';

import SearchBar from '../search-bar/search-bar-container';
import {
    NavBarContainer,
    NavLogo,
    NavBarGridContainer,
    NavBarGridItem,
    NavBarInnerContainer,
    StyledSearchButton,
    NavLogoWrapper,
    ButtonsWrapper,
    SearchBarWrapper,
    MhhNavIcon,
} from './style';
import NavLoginButton from './nav-bar-login-button';
import NavBarMessagesButton from './nav-bar-messages-button';
import { OnlyShowOnSmallScreen } from '../utils/screens.style';
import LoginModal from '../../login-modal/app';
import { StyledDialog } from '../../login-modal/app.style';
import { useModal } from '../../use-modal';
import {
    LoginModalType,
    setIsOpen,
    showForgotPasswordModal,
} from '../../reducers/common/login-modal';
import { shouldShowAccountMenu, switchAccount } from './utils';
import PostAdAction from './post-ad-action';

const StyledGrid = css`
    flex-wrap: wrap;

    ${mediaQuery.from(breakpoints.large)} {
        flex-wrap: nowrap;
    }
`;

export default function NavBar(props: Props) {
    const [registrationSuccess, setRegistrationSuccess] = useState(false);
    const [focusSearchBar, setFocusSearchBar] = useState(false);
    const { openDialog, closeDialog } = useModal();
    const {
        baseConfig,
        showSearchBarOnMobile,
        userData,
        isOpen,
        account,
        accounts,
        isProUser,
    } = useSelector(
        selectReduxState,
        shallowEqual
    );

    const dispatch = useDispatch() as ShellUseDispatch;

    const openSearchModalNonBlocking = React.useCallback(() => {
        window.requestAnimationFrame(() => {
            window.setTimeout(() => {
                dispatch(searchBarAct.toggleSearchModal());
            }, 0);
        });
    }, [dispatch]);

    const loggedIn = userData && userData.userLoggedIn;
    const isMobile = baseConfig.device?.type === 'mobile';
    const pageType = baseConfig?.pageType;
    const isMhh = props.isMhhExperiment;
    const useMhhNavStyle = isMhh && isMobile;

    const showSearchIcon = !showSearchBarOnMobile;
    const showAccountMenu = shouldShowAccountMenu(baseConfig) && isProUser && account;

    React.useEffect(() => {
        trackGA4UserLoginSuccess();
        trackGA4UserRegistrationSuccess();

        const params = new URLSearchParams(window.location.search);
        const resetPassword = params.get('reset-password');
        const isRegistrationSuccessful = params.get('new-registration');

        if (isRegistrationSuccessful) {
            setRegistrationSuccess(true);
            dispatch(setIsOpen(true));
            params.delete('new-registration');
            const newUrl = `${window.location.pathname}?${params.toString()}`;
            window.history.replaceState({}, '', newUrl);
        } else if (resetPassword) {
            dispatch(setIsOpen(true));
            dispatch(showForgotPasswordModal({ value: true, isReset: true }));
            params.delete('reset-password');
            const newUrl = `${window.location.pathname}?${params.toString()}`;
            window.history.replaceState({}, '', newUrl);
        }
    }, []);

    const openSearchBar = () => {
        closeDialog();
        setFocusSearchBar(true);
    };

    return (
        <NavBarContainer
            data-q="nav-bar"
            mhcSticky={props.isMhcSticky}
            mhhStyle={useMhhNavStyle}
        >
            <NavBarInnerContainer mhhStyle={useMhhNavStyle}>
                <NavBarGridContainer container item={false} mhhStyle={useMhhNavStyle}>
                    <NavBarGridItem css={StyledGrid} item justifyContent="space-between" col={12} mhhStyle={useMhhNavStyle}>
                        <NavLogoWrapper mhhStyle={useMhhNavStyle}>
                            <NavLogo
                                linkTo="/"
                                mhhStyle={useMhhNavStyle}
                                {...qaAttribute('gumtree-logo')}
                                data-testid="gumtree-logo"
                                onClick={(e) =>
                                    trackGA4AndNavigate<GA4.ClickPrimaryLogo>({
                                        href: '/',
                                        mouseEvent: e,
                                        payload: {
                                            event: 'click_primary_logo',
                                            linkDomain: getLinkHostname('/'),
                                            linkText: 'Gumtree',
                                            linkURL: '/',
                                        },
                                    })
                                }
                            />
                        </NavLogoWrapper>
                        <ButtonsWrapper mhhStyle={useMhhNavStyle}>
                            {showSearchIcon && (
                                <OnlyShowOnSmallScreen>
                                    <StyledSearchButton
                                        {...qaAttribute('search-bar-toggler')}
                                        aria-label="Search Gumtree.com"
                                        className="search-toggle"
                                        dark
                                        label="Search"
                                        icon={useMhhNavStyle ? <MhhNavIcon type="navbar-search" /> : <Icon type="magnifying-glass" />}
                                        onClick={openSearchModalNonBlocking}
                                        bottomLabel
                                        mhhStyle={useMhhNavStyle}
                                    />
                                </OnlyShowOnSmallScreen>
                            )}
                            <PostAdAction
                                useMhhLayout={false}
                                useMhhNavStyle={useMhhNavStyle}
                                useNewIcons={!!useMhhNavStyle}
                                loggedIn={!!loggedIn}
                                isMobile={isMobile}
                                pageType={pageType}
                                categoryL2={baseConfig.category?.l2Category}
                                buyerUrl={baseConfig.buyerUrl}
                                openDialog={openDialog}
                                sellModalType={LoginModalType.SELL}
                            />
                            {loggedIn && <NavBarMessagesButton isMhhExperiment={useMhhNavStyle} />}
                            {loggedIn && <UserMenu isLoggedIn={loggedIn} pageType={pageType} />}
                            {loggedIn && showAccountMenu && (
                                <AccountMenu
                                    userData={userData}
                                    account={account}
                                    accountSelections={accounts}
                                    onChangeAccount={switchAccount}
                                />
                            )}
                            {!userData && (
                                <>
                                    <>
                                        <NavLoginButton
                                            dark
                                            showTooltip={false}
                                            icon={useMhhNavStyle ? <MhhNavIcon type="navbar-signup" /> : <Icon type="person-outline-register" />}
                                            label="Sign up"
                                            qaAttr="hm-registration"
                                            mhhStyle={useMhhNavStyle}
                                            onClick={(e) => {
                                                openDialog(e, LoginModalType.REGISTER, false);
                                                trackGA4Event<GA4.ClickPrimaryNavigation>({
                                                    event: 'click_primary_navigation',
                                                    linkText: 'sign_up',
                                                    navigationCategory: 'menu',
                                                    navigationHeader: undefined,
                                                    navigationHierarchy: 'header',
                                                    pageClickedFrom: pageType,
                                                });
                                            }}
                                        />
                                        <NavLoginButton
                                            dark
                                            showTooltip={false}
                                            icon={useMhhNavStyle ? <MhhNavIcon type="navbar-login" /> : <Icon type="person-outline-login" />}
                                            label="Login"
                                            mhhStyle={useMhhNavStyle}
                                            onClick={(e) => {
                                                openDialog(e, LoginModalType.LOGIN, true);
                                                trackGA4Event<GA4.ClickPrimaryNavigation>({
                                                    event: 'click_primary_navigation',
                                                    linkText: 'log_in',
                                                    navigationCategory: 'menu',
                                                    navigationHeader: undefined,
                                                    navigationHierarchy: 'header',
                                                    pageClickedFrom: pageType,
                                                });
                                            }}
                                        />
                                    </>
                                </>
                            )}
                            {!props.hideSideMenu && (
                                <SideMenu
                                    baseConfig={baseConfig}
                                    isLoggedIn={!!userData}
                                    openDialog={openDialog}
                                    isMhhExperiment={useMhhNavStyle}
                                />
                            )}
                            {loggedIn && showAccountMenu && (
                                <AccountMenu
                                    mobile
                                    mhhStyle={useMhhNavStyle}
                                    userData={userData}
                                    account={account}
                                    accountSelections={accounts}
                                    onChangeAccount={switchAccount}
                                />
                            )}
                        </ButtonsWrapper>
                        <SearchBarWrapper>
                            <SearchBar outsideFocus={focusSearchBar} />
                        </SearchBarWrapper>
                    </NavBarGridItem>
                </NavBarGridContainer>
            </NavBarInnerContainer>
            <StyledDialog isOpen={isOpen} onClose={closeDialog}>
                <LoginModal
                    registrationSuccess={registrationSuccess}
                    openSearchBar={openSearchBar}
                />
            </StyledDialog>
        </NavBarContainer>
    );
}

interface Props {
    hideSideMenu?: boolean;
    isMhcSticky?: boolean;
    isMhhExperiment?: boolean;
}

NavBar.defaultProps = {
    hideSideMenu: false,
};

function selectReduxState({
    baseConfig,
    categoryNav,
    searchBar,
    features,
    userData,
    accountData,
    loginModal: { isOpen, showForgotPassword },
}: ShellState) {
    return {
        baseConfig,
        categoryNav,
        showSearchBarOnMobile: searchBar.showSearchBarOnMobile,
        features,
        userData,
        isOpen,
        showForgotPassword,
        isProUser: baseConfig.userType === 'PRO',
        account: accountData?.account,
        accounts: accountData?.accountSelections || [],
    };
}
